import valleyV from '../maps/valley-v.json'
import crossroadsC from '../maps/crossroads-c.json'
import switchbackS from '../maps/switchback-s.json'
import riftR from '../maps/rift-r.json'
import testS from '../maps/test-s.json'
import { TEST_DUAL_MAP } from '../maps/test-dual'
import { TEST_TRIPLE_MAP } from '../maps/test-triple'
import { LAB_BUILDERS, LAB_META } from '../maps/labs'
import { PRODUCTION_MAP_BUILDERS } from '../maps/productionMaps'
import type { MapDefinition } from './types'
import { validateMap } from './validateMap'
import type { MapArchetype, MapComplexity } from '../maps/productionArchetypes'
import { planForMapId } from '../maps/productionPlans'

export interface BuiltinMapMeta {
  id: string
  name: string
  /** First-party campaign maps appear on HOME. Dev/test maps do not. */
  campaign: boolean
  /**
   * Experimental layout kept for measurement and playtest only.
   * Never referenced by a campaign stage; safe to reshape between milestones.
   */
  experimental?: boolean
  /** M69 laboratory topology — only via /dev/labs. */
  laboratory?: boolean
  /** Production design archetype (campaign maps). */
  archetype?: MapArchetype
  /** Internal complexity ladder A–D (not player-facing). */
  complexity?: MapComplexity
}

function campaignPlanMeta(id: string): Pick<BuiltinMapMeta, 'archetype' | 'complexity'> {
  const plan = planForMapId(id)
  return plan ? { archetype: plan.archetype, complexity: plan.complexity } : {}
}

/**
 * First-party built-in map registry.
 * Adding a campaign map: define geometry, register here, optionally add balance fixtures.
 * Stage content references mapId — never stageId === mapId.
 */
const BUILTIN_ENTRIES: Array<{ meta: BuiltinMapMeta; definition: MapDefinition }> = [
  {
    meta: { id: 'valley-v', name: 'Valley', campaign: true, ...campaignPlanMeta('valley-v') },
    definition: valleyV as MapDefinition
  },
  {
    meta: {
      id: 'crossroads-c',
      name: 'Crossroads',
      campaign: true,
      ...campaignPlanMeta('crossroads-c')
    },
    definition: crossroadsC as MapDefinition
  },
  {
    meta: {
      id: 'switchback-s',
      name: 'Switchback',
      campaign: true,
      ...campaignPlanMeta('switchback-s')
    },
    definition: switchbackS as MapDefinition
  },
  {
    meta: { id: 'rift-r', name: 'Rift', campaign: true, ...campaignPlanMeta('rift-r') },
    definition: riftR as MapDefinition
  },
  {
    meta: {
      id: 'twinspire',
      name: 'Twinspire',
      campaign: true,
      ...campaignPlanMeta('twinspire')
    },
    definition: PRODUCTION_MAP_BUILDERS.twinspire!()
  },
  {
    meta: {
      id: 'confluence',
      name: 'Confluence',
      campaign: true,
      ...campaignPlanMeta('confluence')
    },
    definition: PRODUCTION_MAP_BUILDERS.confluence!()
  },
  {
    meta: { id: 'delta', name: 'Delta', campaign: true, ...campaignPlanMeta('delta') },
    definition: PRODUCTION_MAP_BUILDERS.delta!()
  },
  {
    meta: { id: 'triad', name: 'Triad', campaign: true, ...campaignPlanMeta('triad') },
    definition: PRODUCTION_MAP_BUILDERS.triad!()
  },
  {
    meta: { id: 'skew', name: 'Skew', campaign: true, ...campaignPlanMeta('skew') },
    definition: PRODUCTION_MAP_BUILDERS.skew!()
  },
  {
    meta: { id: 'test-s', name: 'Test Square', campaign: false },
    definition: testS as MapDefinition
  },
  {
    meta: { id: 'test-dual', name: 'Test Dual Lane', campaign: false, experimental: true },
    definition: TEST_DUAL_MAP
  },
  {
    meta: { id: 'test-triple', name: 'Test Triple Lane', campaign: false, experimental: true },
    definition: TEST_TRIPLE_MAP
  },
  ...LAB_META.map((lab) => ({
    meta: {
      id: lab.id,
      name: lab.name,
      campaign: false,
      experimental: true,
      laboratory: true
    } satisfies BuiltinMapMeta,
    definition: LAB_BUILDERS[lab.id]!()
  }))
]

function cloneDefinition(map: MapDefinition): MapDefinition {
  return structuredClone(map)
}

/** All registered built-in definitions (campaign + test). */
export const BUILTIN_MAPS: MapDefinition[] = BUILTIN_ENTRIES.map((e) => e.definition)

/** Campaign maps shown on HOME (Valley, Crossroads). */
export const availableMaps: MapDefinition[] = BUILTIN_ENTRIES.filter((e) => e.meta.campaign).map(
  (e) => e.definition
)

export const DEFAULT_MAP_ID = availableMaps[0]?.id ?? 'valley-v'

export function listBuiltinMeta(): BuiltinMapMeta[] {
  return BUILTIN_ENTRIES.map((e) => ({ ...e.meta }))
}

export function listCampaignMaps(): MapDefinition[] {
  return availableMaps.map(cloneDefinition)
}

export function listBuiltinMaps(): MapDefinition[] {
  return BUILTIN_MAPS.map(cloneDefinition)
}

/** Experimental layouts — editor playtest and measurement only, never staged. */
export function listExperimentalMaps(): MapDefinition[] {
  return BUILTIN_ENTRIES.filter((e) => e.meta.experimental).map((e) => cloneDefinition(e.definition))
}

/** M69 laboratory maps only. */
export function listLaboratoryMaps(): MapDefinition[] {
  return BUILTIN_ENTRIES.filter((e) => e.meta.laboratory).map((e) => cloneDefinition(e.definition))
}

/** Returns a deep clone so callers cannot mutate registry definitions. */
export function getMap(mapId: string): MapDefinition | undefined {
  const entry = BUILTIN_ENTRIES.find((e) => e.meta.id === mapId || e.definition.id === mapId)
  if (!entry) return undefined
  return cloneDefinition(entry.definition)
}

export function getPlayableMap(mapId: string): MapDefinition | undefined {
  const map = getMap(mapId)
  if (!map) return undefined
  const result = validateMap(map)
  if (!result.ok) return undefined
  return map
}

export function isBuiltinMapId(mapId: string): boolean {
  return BUILTIN_ENTRIES.some((e) => e.meta.id === mapId || e.definition.id === mapId)
}

export function isCampaignMapId(mapId: string): boolean {
  return BUILTIN_ENTRIES.some(
    (e) => e.meta.campaign && (e.meta.id === mapId || e.definition.id === mapId)
  )
}
